import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, catchError, map, switchMap, throwError } from 'rxjs';
import { TuiDay } from '@taiga-ui/cdk';
import {
  LoyaltyService,
  LoyaltyStatisticsByMonths,
  LoyaltyStatisticsByPeriod,
  LoyaltyStatisticsSummary,
  convertDateToApiFormat,
  LoyaltyDiscountsListByPeriod,
} from '@src/api';
import { TuiDayTimeTransformer } from '@src/utils';
import { AlertService } from '@src/core/services';

import {
  LoyaltyDiscountsListByPeriodUI,
  LoyaltyStatisticsBarParams,
  LoyaltyStatisticsDiscountsParams,
  LoyaltyStatisticsProductsParams,
  LoyaltyStatisticsSummaryParams,
} from './types';

@Injectable({
  providedIn: 'root',
})
export class LpStatisticsService {
  private readonly loyaltyService = inject(LoyaltyService);
  private readonly alertService = inject(AlertService);

  /** Общая статистика */
  statisticsSummary$ = new BehaviorSubject<LoyaltyStatisticsSummary | null>(null);
  loadStatisticsSummary$ = new BehaviorSubject<LoyaltyStatisticsSummaryParams | null>(null);

  /** Статистика для графика */
  statisticsBar$ = new BehaviorSubject<LoyaltyStatisticsByMonths | null>(null);
  loadStatisticsBar$ = new BehaviorSubject<LoyaltyStatisticsBarParams | null>(null);

  /** Статистика объявлений */
  statisticsProducts$ = new BehaviorSubject<LoyaltyStatisticsByPeriod | null>(null);
  loadStatisticsProducts$ = new BehaviorSubject<LoyaltyStatisticsProductsParams | null>(null);

  /** Статистика получения скидок */
  statisticsDiscounts$ = new BehaviorSubject<LoyaltyDiscountsListByPeriodUI | null>(null);
  loadStatisticsDiscounts$ = new BehaviorSubject<LoyaltyStatisticsDiscountsParams | null>(null);

  constructor() {
    this.loadStatisticsSummary().subscribe();
    this.loadStatisticsBar().subscribe();
    this.loadStatisticsProducts().subscribe();
    this.loadStatisticsDiscounts().subscribe();
  }

  startStatisticsSummary(params: LoyaltyStatisticsSummaryParams) {
    this.loadStatisticsSummary$.next(params);
  }

  loadStatisticsSummary() {
    return this.loadStatisticsSummary$
      .pipe(
        switchMap(params => {
          return this.loyaltyService.statisticsSummary(
            params?.unionId,
            params?.productId,
            this.convertTuiDayToApiFormat(params?.dateFrom),
            this.convertTuiDayToApiFormat(params?.dateTo),
          );
        }),
      )
      .pipe(
        map(statisticsSummary => {
          this.statisticsSummary$.next(statisticsSummary);
          return statisticsSummary;
        }),
      )
      .pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
      );
  }

  startStatisticsBar(params: LoyaltyStatisticsBarParams) {
    this.loadStatisticsBar$.next(params);
  }

  loadStatisticsBar() {
    return this.loadStatisticsBar$
      .pipe(
        switchMap(params => {
          return this.loyaltyService.statisticsByMonths(
            params?.unionId,
            params?.productId,
            this.convertTuiDayToApiFormat(params?.dateFrom),
            this.convertTuiDayToApiFormat(params?.dateTo),
          );
        }),
      )
      .pipe(
        map(statisticsBar => {
          this.statisticsBar$.next(statisticsBar);
          return statisticsBar;
        }),
      )
      .pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
      );
  }

  startStatisticsProducts(params: LoyaltyStatisticsProductsParams) {
    this.loadStatisticsProducts$.next(params);
  }

  loadStatisticsProducts() {
    return this.loadStatisticsProducts$
      .pipe(
        switchMap(params => {
          return this.loyaltyService.statisticsByPeriod(
            params?.unionId,
            params?.productId,
            this.convertTuiDayToApiFormat(params?.dateFrom),
            this.convertTuiDayToApiFormat(params?.dateTo),
          );
        }),
      )
      .pipe(
        map(statisticsProducts => {
          this.statisticsProducts$.next(statisticsProducts);
          return statisticsProducts;
        }),
      )
      .pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
      );
  }

  startStatisticsDiscounts(params: LoyaltyStatisticsDiscountsParams) {
    this.loadStatisticsDiscounts$.next(params);
  }

  loadStatisticsDiscounts() {
    return this.loadStatisticsDiscounts$
      .pipe(
        switchMap(params => {
          return this.loyaltyService.discountsListByPeriod(
            params?.unionId,
            params?.productId,
            this.convertTuiDayToApiFormat(params?.dateFrom),
            this.convertTuiDayToApiFormat(params?.dateTo),
          );
        }),
      )
      .pipe(
        map(statisticsDiscounts => this.normalizeStatisticsDiscountsUI(statisticsDiscounts)),
        map(statisticsDiscounts => {
          this.statisticsDiscounts$.next(statisticsDiscounts);
          return statisticsDiscounts;
        }),
      )
      .pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
      );
  }

  clearAll() {
    this.statisticsSummary$.next(null);
    this.statisticsBar$.next(null);
    this.statisticsProducts$.next(null);
    this.statisticsDiscounts$.next(null);
  }

  private convertTuiDayToApiFormat(value?: TuiDay) {
    return value ? convertDateToApiFormat(TuiDayTimeTransformer.tuiDayToDate(value)) : undefined;
  }

  private normalizeStatisticsDiscountsUI(data: LoyaltyDiscountsListByPeriod) {
    if (data.entries?.length) {
      data.entries = data.entries.map(item => {
        return {
          ...item,
          discount: item.discountRate
            ? item.discountRate + ' %'
            : item.discountAbsolute
            ? item.discountAbsolute + ' ₽'
            : item.discountText,
        };
      });
    }

    return data;
  }
}
